import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { adminGetClientErrors, adminClearClientErrors } from '../../lib/api';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { AlertTriangle, Loader2, RotateCw, Trash2, Mail, User as UserIcon, Globe, Monitor, Code } from 'lucide-react';
import { toast } from 'sonner';

function fmtDate(d) { return d ? new Date(d).toLocaleString('fr-FR') : '—'; }
function fmtRel(d) {
  if (!d) return '';
  const diff = (Date.now() - new Date(d).getTime()) / 1000;
  if (diff < 60) return `il y a ${Math.floor(diff)}s`;
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return `il y a ${Math.floor(diff / 86400)} j`;
}

export default function AdminClientErrors() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [errors, setErrors] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user || !isAdmin) { navigate('/admin/login'); return; }
    load();
    const id = setInterval(() => load(true), 30000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, loading]);

  const load = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await adminGetClientErrors(200);
      setErrors(res.data?.errors || []);
    } catch {
      if (!silent) toast.error('Erreur de chargement');
    } finally {
      if (!silent) setRefreshing(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Effacer TOUTES les erreurs client enregistrées ?')) return;
    try {
      const res = await adminClearClientErrors();
      toast.success(`${res.data.deleted} erreurs supprimées`);
      await load();
    } catch {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex bg-cream">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-gold" />
        </div>
      </div>
    );
  }

  // Group errors by message for quick triage
  const groups = {};
  errors.forEach((e) => {
    const key = e.error_message || e.error_name || 'Unknown';
    if (!groups[key]) groups[key] = [];
    groups[key].push(e);
  });
  const groupedList = Object.entries(groups).sort((a, b) => b[1].length - a[1].length);

  return (
    <div className="min-h-screen flex bg-cream">
      <AdminSidebar />
      <main className="flex-1 p-5 lg:p-8 overflow-x-hidden">
        <div className="max-w-6xl mx-auto pl-12 lg:pl-0">
          <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="font-display text-3xl font-bold text-rolex-green mb-1 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-amber-600" />
                Erreurs client
              </h1>
              <p className="text-rolex-green/65 text-sm">
                {errors.length} erreur{errors.length !== 1 ? 's' : ''} reportée{errors.length !== 1 ? 's' : ''} par les navigateurs (max 1000 conservées).
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => load()} disabled={refreshing} className="gap-2">
                {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />} Rafraîchir
              </Button>
              {errors.length > 0 && (
                <Button variant="outline" onClick={handleClear} className="border-red-300 text-red-700 hover:bg-red-50 gap-2">
                  <Trash2 className="w-4 h-4" /> Tout effacer
                </Button>
              )}
            </div>
          </div>

          {errors.length === 0 ? (
            <Card className="border border-gold/20 bg-white">
              <CardContent className="p-16 text-center text-rolex-green/55">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-semibold">Aucune erreur récente 🎉</p>
                <p className="text-xs mt-1">Les bugs JavaScript des clients apparaîtront ici automatiquement.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {groupedList.map(([msg, items]) => {
                const first = items[0];
                const isOpen = expanded === msg;
                return (
                  <Card key={msg} className="border border-gold/20 bg-white">
                    <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded(isOpen ? null : msg)}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge className="bg-red-500 text-white border-none">{first.error_name || 'Error'}</Badge>
                            <Badge className="bg-amber-500 text-white border-none">{items.length}× occurrences</Badge>
                            <span className="text-[10px] text-rolex-green/55 font-mono">dernière: {fmtRel(first.server_ts)}</span>
                          </div>
                          <CardTitle className="text-sm font-mono break-words text-red-700 leading-snug">
                            {msg.length > 200 ? msg.substring(0, 200) + '…' : msg}
                          </CardTitle>
                        </div>
                        <Button size="sm" variant="ghost" className="shrink-0">
                          {isOpen ? '▲' : '▼'}
                        </Button>
                      </div>
                    </CardHeader>
                    {isOpen && (
                      <CardContent className="space-y-3 pt-0">
                        <div className="border-t border-gold/10 pt-3 space-y-3">
                          {items.slice(0, 10).map((e, i) => (
                            <div key={i} className="rounded-lg bg-cream border border-gold/15 p-3 space-y-2">
                              <div className="flex items-center gap-3 flex-wrap text-xs">
                                <span className="text-rolex-green/65 font-mono">{fmtDate(e.server_ts)}</span>
                                {e.user_email && (
                                  <span className="flex items-center gap-1 text-rolex-green">
                                    <UserIcon className="w-3 h-3" />
                                    <span className="font-semibold">{e.user_name || e.user_email}</span>
                                  </span>
                                )}
                                {e.user_email && (
                                  <span className="flex items-center gap-1 text-rolex-green/65">
                                    <Mail className="w-3 h-3" /> {e.user_email}
                                  </span>
                                )}
                              </div>
                              {e.url && (
                                <p className="text-[10px] text-rolex-green/65 flex items-center gap-1.5">
                                  <Globe className="w-3 h-3 shrink-0" /> <a href={e.url} target="_blank" rel="noopener noreferrer" className="underline hover:text-rolex-green truncate">{e.url}</a>
                                </p>
                              )}
                              {e.user_agent && (
                                <p className="text-[10px] text-rolex-green/65 flex items-center gap-1.5">
                                  <Monitor className="w-3 h-3 shrink-0" /> <span className="truncate">{e.user_agent}</span>
                                </p>
                              )}
                              {e.component_stack && (
                                <details className="text-[10px] font-mono">
                                  <summary className="cursor-pointer text-rolex-green/70 hover:text-rolex-green flex items-center gap-1">
                                    <Code className="w-3 h-3" /> Stack trace
                                  </summary>
                                  <pre className="mt-2 whitespace-pre-wrap text-[10px] opacity-80 max-h-60 overflow-auto bg-white p-2 rounded border border-gold/15">
                                    {e.component_stack}
                                  </pre>
                                </details>
                              )}
                              {e.stack && (
                                <details className="text-[10px] font-mono">
                                  <summary className="cursor-pointer text-rolex-green/70 hover:text-rolex-green flex items-center gap-1">
                                    <Code className="w-3 h-3" /> JS stack
                                  </summary>
                                  <pre className="mt-2 whitespace-pre-wrap text-[10px] opacity-80 max-h-60 overflow-auto bg-white p-2 rounded border border-gold/15">
                                    {e.stack}
                                  </pre>
                                </details>
                              )}
                            </div>
                          ))}
                          {items.length > 10 && (
                            <p className="text-[10px] text-rolex-green/55 italic text-center">+{items.length - 10} occurrences masquées</p>
                          )}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
